import { Type } from 'class-transformer';
import { IsNotEmpty, IsString, IsOptional, IsEnum, MaxLength, IsBoolean, IsArray, ValidateNested } from 'class-validator';
import { OrganizationType } from '../../../enums/clients';
import { SampleRetentionAnnex } from '../../../enums/sample-retention-annex.enum';
import { OrganizationContactRequestDto } from './organization-contact.request.dto';

export class CreateOrganizationRequestDto {
    /**
     * Name of the organization
     * @example 'ACME Pharmaceuticals Canada Inc.'
     */
    @IsNotEmpty()
    @IsString()
    @MaxLength(255)
    organizationName: string;

    /**
     * Physical address of the organization
     * @example '123 Business Parkway, Suite 400, Toronto'
     */
    @IsOptional()
    @IsString()
    @MaxLength(500)
    address?: string;

    /**
     * Type of organization (MANUFACTURER, IMPORTER, DISTRIBUTOR, etc.)
     * @example 'IMPORTER'
     */
    @IsNotEmpty()
    @IsEnum(OrganizationType)
    organizationType: OrganizationType;

    /**
     * Whether organization has a Drug Establishment Licence
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    hasDel?: boolean;

    /**
     * Drug Establishment Licence Number
     * @example '3-001234 - B'
     */
    @IsOptional()
    @IsString()
    @MaxLength(100)
    delNumber?: string;

    /**
     * Whether organization has an NHP Site Licence
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    hasNhpSiteLicence?: boolean;

    /**
     * Natural Health Product Site Licence Number
     * @example 'NHP-001234'
     */
    @IsOptional()
    @IsString()
    @MaxLength(100)
    nhpSiteLicence?: string;

    /**
     * Whether organization has a Medical Device Establishment Licence
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    hasMdel?: boolean;

    /**
     * Medical Device Establishment Licence Number
     * @example 'MDEL-001234'
     */
    @IsOptional()
    @IsString()
    @MaxLength(100)
    mdelNumber?: string;

    /**
     * Whether organization has a Dealer's Licence
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    hasDealersLicence?: boolean;

    /**
     * Dealer's Licence Number
     * @example 'DL-001234'
     */
    @IsOptional()
    @IsString()
    @MaxLength(100)
    dealersLicence?: string;

    /**
     * Whether license is available for this organization
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    isLicenseAvailable?: boolean;

    @IsOptional()
    @IsString()
    @MaxLength(255)
    qualityAgreement?: string;

    @IsOptional()
    @IsBoolean()
    isWholesaleLicenseConfirmed?: boolean;

    @IsOptional()
    @IsString()
    @MaxLength(256)
    wholesaleLicenseMethod?: string;

    /**
     * Testing Service - API (TESTER type only)
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    testingServiceApi?: boolean;

    /**
     * Testing Service - ID (TESTER type only)
     * @example true
     */
    @IsOptional()
    @IsBoolean()
    testingServiceId?: boolean;

    /**
     * Testing Service - Confirmatory (TESTER type only)
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    testingServiceConfirmatory?: boolean;

    /**
     * Testing Service - Stability (TESTER type only)
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    testingServiceStability?: boolean;

    /**
     * Special Laboratory Notes (TESTER type only)
     * @example 'QCO to also follow Jazz Ireland Guidance...'
     */
    @IsOptional()
    @IsString()
    specialLabNotes?: string;

    /**
     * Lot release blocked until confirmatory testing complete (TESTER type only)
     * @example false
     */
    @IsOptional()
    @IsBoolean()
    lotReleaseBlockedUntilConfirmatory?: boolean;

    /**
     * Lot release comments (TESTER type only)
     * @example 'Additional testing requirements...'
     */
    @IsOptional()
    @IsString()
    lotReleaseComments?: string;

    /**
     * General notes for the organization
     * @example 'Primary importer for European pharmaceutical products'
     */
    @IsOptional()
    @IsString()
    notes?: string;

    @IsOptional()
    @IsEnum(SampleRetentionAnnex)
    sampleRetentionAnnex?: SampleRetentionAnnex;

    @IsOptional()
    @IsString()
    @MaxLength(1000)
    sampleRetentionComments?: string;

    /**
     * Additional Licensable Activities (free text)
     * @example 'Importer, Distributor'
     */
    @IsOptional()
    @IsString()
    additionalLicensableActivities?: string;

    @IsOptional()
    @IsArray()
    @ValidateNested({ each: true })
    @Type(() => OrganizationContactRequestDto)
    contacts?: OrganizationContactRequestDto[];
}
